import { Text, Group, Button, ActionIcon, Menu, Badge, Tooltip, Modal, Stack, Table, Box } from '@mantine/core';
import { IconDots, IconEdit, IconTrash, IconGitBranch, IconRocket, IconRocketOff, IconChevronDown, IconFileText, IconArchive, IconArchiveOff, IconTrashOff, IconAlertTriangle, IconTemplate } from '@tabler/icons-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Flow } from '@/services/flows.api';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { flowsApi } from '@/services/flows.api';
import type { FlowFilter } from '@/layouts/DashboardLayout';
import { notifications } from '@mantine/notifications';
import { CopyFlowModal } from './CopyFlowModal';
import { useTemplates } from '../hooks/useTemplates';

interface FlowCardProps {
  flow: Flow;
  activeFilter: FlowFilter;
  onEdit: (flow: Flow) => void;
  onDelete: (flow: Flow) => void;
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatFullDate(dateString: string): string {
  return new Date(dateString).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function FlowCard({ flow, activeFilter, onEdit, onDelete }: FlowCardProps) {
  const [versionsModalOpen, setVersionsModalOpen] = useState(false);
  const [saveAsTemplateOpen, setSaveAsTemplateOpen] = useState(false);
  const { saveAsTemplate } = useTemplates(false);
  const queryClient = useQueryClient();
  const latestVersion = flow.versions?.[0];
  const versionCount = flow.versions?.length || 0;
  const publishedVersion = flow.versions?.find(v => v.id === flow.publishedVersionId);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['flows'] });
    queryClient.invalidateQueries({ queryKey: ['flows-trashed'] });
  };

  const publishMutation = useMutation({
    mutationFn: (versionId: string) => flowsApi.publishFlow(flow.id, versionId),
    onSuccess: invalidate,
  });

  const unpublishMutation = useMutation({
    mutationFn: () => flowsApi.unpublishFlow(flow.id),
    onSuccess: invalidate,
  });

  const archiveMutation = useMutation({
    mutationFn: () => flowsApi.archiveFlow(flow.id),
    onSuccess: invalidate,
  });

  const restoreArchiveMutation = useMutation({
    mutationFn: () => flowsApi.restoreFromArchive(flow.id),
    onSuccess: invalidate,
  });

  const trashMutation = useMutation({
    mutationFn: () => flowsApi.trashFlow(flow.id),
    onSuccess: invalidate,
  });

  const restoreMutation = useMutation({
    mutationFn: () => flowsApi.restoreFlow(flow.id),
    onSuccess: invalidate,
  });

  const permanentDeleteMutation = useMutation({
    mutationFn: () => flowsApi.permanentlyDeleteFlow(flow.id),
    onSuccess: invalidate,
  });

  const isLive = !!publishedVersion;
  const isArchived = latestVersion?.status === 'ARCHIVED';
  const statusLabel = isLive ? 'Live' : isArchived ? 'Archived' : latestVersion?.status === 'DRAFT' ? 'Draft' : latestVersion?.status?.toLowerCase() || 'Draft';
  const statusColor = isLive ? '#22b783' : isArchived ? '#e6a817' : '#4E5A6B';
  const isTrashView = activeFilter === 'trash';
  const isArchivedView = activeFilter === 'archived';

  return (
    <>
      <Box
        px="lg"
        py="md"
        style={{
          borderBottom: '1px solid #F2F4F7',
          cursor: 'pointer',
          transition: 'background-color 0.15s',
          '&:hover': { backgroundColor: '#FAFBFC' },
        }}
        component={Link}
        to={`/flows/${flow.id}`}
        onClick={(e: any) => {
          // Don't navigate if clicking on buttons/menus
          if ((e.target as HTMLElement).closest('button, [role="menu"], [data-stop-propagation]')) {
            e.preventDefault();
          }
        }}
      >
        <Group justify="space-between" align="center" wrap="nowrap">
          {/* Flow Name + Description */}
          <Box style={{ flex: 2, minWidth: 0 }}>
            <Group gap="sm" wrap="nowrap">
              <Box
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  backgroundColor: '#e8edf5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <IconFileText size={18} style={{ color: '#415A91' }} />
              </Box>
              <Box style={{ minWidth: 0 }}>
                <Text fw={600} size="sm" c="#2A3140" truncate style={{ textDecoration: 'none' }}>
                  {flow.name}
                </Text>
                <Text size="xs" c="#4E5A6B" truncate>
                  {flow.description || 'No description'}
                </Text>
              </Box>
            </Group>
          </Box>

          {/* Last Updated */}
          <Box style={{ flex: 1, minWidth: 120 }}>
            <Tooltip label={formatFullDate(flow.updatedAt)} withArrow>
              <Text size="sm" c="#4E5A6B">{formatDate(flow.updatedAt)}</Text>
            </Tooltip>
          </Box>

          {/* Version */}
          <Box style={{ width: 100 }}>
            <Badge
              size="sm"
              variant="light"
              color="hollowayPrimary"
              style={{ cursor: 'pointer' }}
              data-stop-propagation
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setVersionsModalOpen(true);
              }}
            >
              v{latestVersion?.version || 1}.{versionCount}
            </Badge>
          </Box>

          {/* Status */}
          <Box style={{ width: 100 }}>
            <Group gap={6}>
              <Box
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: statusColor,
                }}
              />
              <Text size="sm" c="#2A3140" style={{ textTransform: 'capitalize' }}>
                {statusLabel}
              </Text>
            </Group>
          </Box>

          {/* Actions */}
          <Box style={{ width: 100 }} data-stop-propagation>
            <Menu position="bottom-end">
              <Menu.Target>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  aria-label={`Actions for ${flow.name}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                >
                  <IconDots size={16} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                {isTrashView ? (
                  <>
                    <Menu.Item
                      leftSection={<IconTrashOff size={14} />}
                      onClick={() => restoreMutation.mutate()}
                      disabled={restoreMutation.isPending}
                    >
                      Restore
                    </Menu.Item>
                    <Menu.Divider />
                    <Menu.Item
                      leftSection={<IconAlertTriangle size={14} />}
                      color="red"
                      onClick={() => permanentDeleteMutation.mutate()}
                      disabled={permanentDeleteMutation.isPending}
                    >
                      Delete Permanently
                    </Menu.Item>
                  </>
                ) : isArchivedView ? (
                  <>
                    <Menu.Item
                      leftSection={<IconArchiveOff size={14} />}
                      onClick={() => restoreArchiveMutation.mutate()}
                      disabled={restoreArchiveMutation.isPending}
                    >
                      Restore to Draft
                    </Menu.Item>
                    <Menu.Divider />
                    <Menu.Item
                      leftSection={<IconTrash size={14} />}
                      color="red"
                      onClick={() => trashMutation.mutate()}
                      disabled={trashMutation.isPending}
                    >
                      Move to Trash
                    </Menu.Item>
                  </>
                ) : (
                  <>
                    <Menu.Item leftSection={<IconEdit size={14} />} onClick={() => onEdit(flow)}>
                      Edit
                    </Menu.Item>
                    <Menu.Item leftSection={<IconGitBranch size={14} />} onClick={() => setVersionsModalOpen(true)}>
                      Versions
                    </Menu.Item>
                    <Menu.Item leftSection={<IconTemplate size={14} />} onClick={() => setSaveAsTemplateOpen(true)}>
                      Save as template
                    </Menu.Item>
                    {!isLive && !isArchived && (
                      <Menu.Item
                        leftSection={<IconArchive size={14} />}
                        onClick={() => archiveMutation.mutate()}
                        disabled={archiveMutation.isPending}
                      >
                        Archive
                      </Menu.Item>
                    )}
                    {isArchived && (
                      <Menu.Item
                        leftSection={<IconArchiveOff size={14} />}
                        onClick={() => restoreArchiveMutation.mutate()}
                        disabled={restoreArchiveMutation.isPending}
                      >
                        Restore to Draft
                      </Menu.Item>
                    )}
                    <Menu.Divider />
                    <Menu.Item
                      leftSection={<IconTrash size={14} />}
                      color="red"
                      onClick={() => trashMutation.mutate()}
                      disabled={trashMutation.isPending}
                    >
                      Move to Trash
                    </Menu.Item>
                  </>
                )}
              </Menu.Dropdown>
            </Menu>
          </Box>
        </Group>
      </Box>

      <Modal
        opened={versionsModalOpen}
        onClose={() => setVersionsModalOpen(false)}
        title={<Text fw={600}>Versions - {flow.name}</Text>}
        size="xl"
      >
        <Stack gap="md">
          {flow.versions && flow.versions.length > 0 ? (
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Version</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Created By</Table.Th>
                  <Table.Th>Created At</Table.Th>
                  <Table.Th>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {flow.versions.map((version, index) => {
                  const isPublished = version.id === flow.publishedVersionId;
                  return (
                    <Table.Tr key={version.id}>
                      <Table.Td>
                        <Group gap="xs">
                          <Text fw={500}>v{version.version}</Text>
                          {index === 0 && <Badge size="xs" variant="light">latest</Badge>}
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <Badge
                          size="sm"
                          color={
                            version.status === 'PUBLISHED' ? 'green' :
                            version.status === 'DRAFT' ? 'gray' :
                            version.status === 'IN_REVIEW' ? 'yellow' : 'red'
                          }
                        >
                          {version.status}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{version.createdBy || 'Unknown'}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Tooltip label={formatFullDate(version.createdAt)} withArrow>
                          <Text size="sm">{formatDate(version.createdAt)}</Text>
                        </Tooltip>
                      </Table.Td>
                      <Table.Td>
                        <Group gap={0}>
                          <Button
                            component={Link}
                            to={`/flows/${flow.id}?version=${version.id}`}
                            size="xs"
                            variant="light"
                            styles={{ root: { borderTopRightRadius: 0, borderBottomRightRadius: 0 } }}
                          >
                            View
                          </Button>
                          <Menu position="bottom-end" withinPortal>
                            <Menu.Target>
                              <ActionIcon
                                size="xs"
                                variant="light"
                                h={30}
                                styles={{ root: { borderTopLeftRadius: 0, borderBottomLeftRadius: 0, borderLeft: '1px solid rgba(0,0,0,0.1)' } }}
                              >
                                <IconChevronDown size={12} />
                              </ActionIcon>
                            </Menu.Target>
                            <Menu.Dropdown>
                              {isPublished ? (
                                <Menu.Item
                                  leftSection={<IconRocketOff size={14} />}
                                  color="red"
                                  onClick={() => unpublishMutation.mutate()}
                                >
                                  Unpublish this version
                                </Menu.Item>
                              ) : (
                                <Menu.Item
                                  leftSection={<IconRocket size={14} />}
                                  color="green"
                                  onClick={() => publishMutation.mutate(version.id)}
                                >
                                  Publish this version
                                </Menu.Item>
                              )}
                            </Menu.Dropdown>
                          </Menu>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          ) : (
            <Text c="dimmed">No versions found</Text>
          )}
        </Stack>
      </Modal>

      <CopyFlowModal
        opened={saveAsTemplateOpen}
        onClose={() => setSaveAsTemplateOpen(false)}
        title="Save as template"
        description="Copies this flow's latest saved version into a new template. This flow is not changed."
        confirmLabel="Save template"
        defaultName={`${flow.name} template`}
        onConfirm={async (name) => {
          await saveAsTemplate.mutateAsync({ id: flow.id, name });
          notifications.show({ title: 'Template created', message: 'The original flow is unchanged.' });
        }}
      />
    </>
  );
}
